import MoveComponent from "../../../core/gd-components/base-component/move-component"

import DndManager from "./managers/dnd-manager"
import {DndType} from "./@types"
import {addClass, removeClass} from "../../gd/utils/dom-and-react/react-utils"

export type DNDComponentProps = {
    data?                       : any         // some component data

    dndType?                    : DndType //'target' | 'source' | 'both' | 'disabled'
    dndManager?                 : DndManager

    dndZIndex?                  : number
    dragTriggerDelta?           : number

    disableMouseMoveListeners?  : boolean // from MoveComponent
    disableMouseEnterListeners? : boolean // from HoverComponent

    onClick?                    : func

    //target
    onDraggedOver?              : func
    onDrop?                     : func
}

export default class DNDComponent<T extends DNDComponentProps> extends MoveComponent<T> {

    dragTriggerDelta: number

    mouseDownEvent  : any
    dragStarted     : boolean = false

    static defaultProps = {
        dndType             : 'both',
        dndZIndex           : 0,
        dragTriggerDelta    : 0,
    }

    constructor(props: DNDComponentProps) {
        super(props);

        // components do not inherit defaultProps
        this.dragTriggerDelta = !_.isNil(this.props.dragTriggerDelta) ? this.props.dragTriggerDelta : 0;

        this._bind('getDraggedOverPreviewType', 'isDraggedItem', 'canDrag', 'canDrop', 'onDragStart', 'onDrag', 'onDragEnd',
            'onDraggedOver', 'onDraggedOut', 'onDrop', 'getInitialRect', 'onClick');
    }

    componentDidMount() {
        super.componentDidMount()
        if (['target','both'].includes(this.props.dndType!)) {
            this.props.dndManager?.addDropTarget(this)
        }

        if (['source','both'].includes(this.props.dndType!)) {
            this.props.dndManager?.addDragSource(this)
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount()
        if (['target','both'].includes(this.props.dndType!)) {
            this.props.dndManager?.removeDropTarget(this)
        }

        if (['source','both'].includes(this.props.dndType!)) {
            this.props.dndManager?.removeDragSource(this)
        }

        this._safariFixTextCursorWhileDragging(true)
    }


    get dndId():ID {
        // return this.props.data?.id || 'unknown-dnd-id'
        return this.getDNDId() || 'unknown-dnd-id'
    }

    getDNDId = () => {
        return this.props.data?.id
    }

    //for target only to determine dragged item preview type while dragged over
    getDraggedOverPreviewType() {
        return (this.props.dndManager && ['target','both'].includes(this.props.dndType!)) ? 'default' : null
    }

    copyDraggedItemPositions(component: any) {
        const lmp = component.getLastMousePosition()
        this.setLastMousePosition({clientX: lmp.x, clientY: lmp.y})
        const sp = component.getStartPosition()
        this.setStartPosition(sp)
    }

    isDraggedItem(id: ID) {
        return this.props.dndManager && this.props.dndManager.isDraggedItem(id);
    }

    setPosition(x: number, y: number, width: number) {
        //do nothing
    }

    onMouseDown(e: MouseEvent, callback: func) {
        this._safariFixTextCursorWhileDragging(false)

        this.mouseDownEvent = e // for onClick available when drag is not available
        this._addWindowListeners()

        this.setLastMousePosition(e)
        this.setStartPosition()

        // only main (left) mouse button is allowed
        if (e.button !== 0) return

        if(!this.canDrag(e)) return
        // e.stopPropagation(); // used to prevent propagation to gantt/workload scroll drag feature while dnd is in progress, but it caused popover onClickOutside not working
        // e.preventDefault(); // should be commented out for hours allocated input to receive focus

        this.dragStarted = true
        this.props.dndManager?.setDragStarted(true)
    }

    onMouseMove(e: MouseEvent, callback: func) {
        const delta: any = this.getMousePositionDelta(e)

        if (['source','both'].includes(this.props.dndType!) &&
            this.mouseDownEvent &&
            (Math.abs(delta.dx) > this.dragTriggerDelta || Math.abs(delta.dy) > this.dragTriggerDelta)) {
            if (this.dragStarted) {
                this.props.dndManager?.dragStart(this, this.mouseDownEvent)
            }
            this.mouseDownEvent = null //drag started then no onClick event
        }
    }

    onMouseUp(e: any, callback: func) {
        this._safariFixTextCursorWhileDragging(true)

        if (this.mouseDownEvent) {
            this.props.dndManager?.setDragStarted(false)
            this.onClick(e)
        }

        this._removeWindowListeners()
        this.mouseDownEvent = null
        this.dragStarted = false
    }

    /*onMouseEnter(e, callback) {
        super.onMouseEnter(e, callback);
        this._safariFixTextCursorWhileDragging(false);
    }

    onMouseLeave(e, callback) {
        super.onMouseLeave(e, callback);

        if (!this.isDraggedItem(this.getDNDId()))
            this._safariFixTextCursorWhileDragging(true);
    }*/

    onMouseLeave(e?: MouseEvent, callback?: callback) {
        super.onMouseLeave(e, callback)

        this.mouseDownEvent = null //cursor left component borders then no onClick event
    }

    _safariFixTextCursorWhileDragging = (value: any) => {
        document.onselectstart = () => value
    }

    canDrag(e?: MouseEvent) {
        return ['source','both'].includes(this.props.dndType!)
    }

    canDrop(draggedItem: any) {
        return ['target','both'].includes(this.props.dndType!)
    }

    // is triggered on this item is started dragging
    onDragStart(): any {
        addClass(this.componentEl, 'is-dragged')
        this.onMouseLeave()
        return null
    }

    // is triggered when this item is dragged
    onDrag(dx: number,dy: number) {
        /*should be overridden if necessary*/
    }

    // is triggered when this item is stopped being dragged
    onDragEnd(mouseEvent: MouseEvent, dx: number, dy: number, dropResult: any) {
        removeClass(this.componentEl, 'is-dragged')
    }

    // is triggered when dragged item is over
    onDraggedOver(draggedItem: any, mouseEvent: MouseEvent, dragStartData?: any) {
        addClass(this.componentEl, 'is-dragged-over')
    }

    // is triggered on each mouse move while dragged item is moved over this item
    onDraggingOver(draggedItem: any, mouseEvent: MouseEvent, dragStartData?: any) {
        /*should be overridden if necessary*/
    }

    // is triggered when dragged item is not over any more, isOnDrop - if is called on drop
    onDraggedOut(draggedItem: any, isOnDrop: boolean) {
        removeClass(this.componentEl, 'is-dragged-over')
    }

    // is triggered when dragged item is dropped to this item
    onDrop(draggedItem: any, dropResult: any) {
        /*should be overridden if necessary*/
        return dropResult
    }

    // special click event because of mouseup and mousedown events are modified and used for DND
    onClick(e: MouseEvent) {
        /*should be overridden if necessary*/
    }

    // the rect the manager hit tests the cursor against. Override it where the elements do not tile -
    // a margin between them leaves a strip that belongs to no target, and a cursor in it drags over
    // nothing at all.
    getDropRect() {
        return this.componentEl.getBoundingClientRect()
    }

    getInitialRect() {
        const rect = this.componentEl.getBoundingClientRect()
        return {
            left    : rect.left,
            top     : rect.top,
            width   : rect.width,
            height  : rect.height
        }
    }
}